import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { MeLeagueStats, DateFormat } from '@shootout-comps/types';
import PasswordInput from '../components/PasswordInput';

interface UserProfilePageProps {
  onBack: () => void;
  onPrivacy?: () => void;
}

const card: React.CSSProperties = {
  background: 'linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))',
  border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
  padding: '1.25rem 1.5rem', marginBottom: '1.25rem',
};
const labelSty: React.CSSProperties = {
  fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase',
  letterSpacing: '0.1em', display: 'block', marginBottom: '0.35rem',
};
const inputSty: React.CSSProperties = {
  width: '100%', padding: '0.7rem 0.9rem', background: 'rgba(0,0,0,0.3)',
  border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '8px',
  color: 'var(--chalk)', outline: 'none', boxSizing: 'border-box',
};
const goldBtn: React.CSSProperties = {
  background: 'linear-gradient(135deg,var(--gold),var(--gold-light))',
  color: 'var(--baize-dark)', border: 'none', borderRadius: '8px',
  padding: '0.7rem 1.5rem', fontSize: '0.9rem', fontWeight: 700,
  cursor: 'pointer', minHeight: '44px',
};

export default function UserProfilePage({ onBack, onPrivacy }: UserProfilePageProps) {
  const { user, getAccessToken, requestEmailChange, fetchLeagueStats, updateDateFormat } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  // ── Date format ────────────────────────────────────────────────────────────
  const [dateFmtSaving, setDateFmtSaving] = useState<DateFormat | null>(null);
  const [dateFmtErr,    setDateFmtErr]    = useState('');

  const handleSetDateFormat = async (fmt: DateFormat) => {
    if (fmt === (user?.dateFormat ?? 'DMY')) return;
    setDateFmtSaving(fmt); setDateFmtErr('');
    try {
      await updateDateFormat(fmt);
    } catch (err) {
      setDateFmtErr(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setDateFmtSaving(null);
    }
  };

  // Which leagues/tournaments this account has been linked to as a player (by an
  // owner/admin, or self-claimed) — see GET /api/auth/me/league-stats.
  const [leagueStats, setLeagueStats] = useState<MeLeagueStats | null>(null);
  useEffect(() => { fetchLeagueStats().then(setLeagueStats).catch(() => null); }, [fetchLeagueStats]);

  // ── Change email ──────────────────────────────────────────────────────────
  const [newEmail,       setNewEmail]       = useState('');
  const [emailSending,   setEmailSending]   = useState(false);
  const [emailMsg,       setEmailMsg]       = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const handleChangeEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setEmailSending(true); setEmailMsg(null);
    try {
      const msg = await requestEmailChange(newEmail.trim());
      setEmailMsg({ type: 'ok', text: msg });
      setNewEmail('');
    } catch (err) {
      setEmailMsg({ type: 'err', text: err instanceof Error ? err.message : 'Failed to send verification' });
    } finally {
      setEmailSending(false);
    }
  };

  // ── Change password (inline, needs current password) ─────────────────────
  const [curPw,     setCurPw]     = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [confPw,    setConfPw]    = useState('');
  const [pwSaving,  setPwSaving]  = useState(false);
  const [pwMsg,     setPwMsg]     = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const handleChangePw = async (e: FormEvent) => {
    e.preventDefault();
    if (newPw !== confPw) { setPwMsg({ type: 'err', text: 'New passwords do not match.' }); return; }
    if (newPw.length < 8)  { setPwMsg({ type: 'err', text: t.passwordMinLength }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      const token = await getAccessToken();
      const res = await fetch('/api/auth/me/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ currentPassword: curPw, newPassword: newPw }),
      });
      const data = await res.json() as { message?: string; error?: string };
      if (!res.ok) { setPwMsg({ type: 'err', text: data.error ?? 'Failed' }); }
      else { setPwMsg({ type: 'ok', text: data.message ?? t.passwordChanged }); setCurPw(''); setNewPw(''); setConfPw(''); }
    } catch { setPwMsg({ type: 'err', text: 'Network error' }); }
    finally { setPwSaving(false); }
  };

  // ── Request password reset by email ──────────────────────────────────────
  const [resetSending, setResetSending] = useState(false);
  const [resetSent,    setResetSent]    = useState(false);
  const [resetErr,     setResetErr]     = useState('');

  const handleRequestReset = async () => {
    if (!user?.email) return;
    setResetSending(true); setResetSent(false); setResetErr('');
    try {
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      const data = await res.json() as { message?: string; error?: string };
      if (!res.ok) { setResetErr(data.error ?? 'Failed'); }
      else { setResetSent(true); }
    } catch { setResetErr('Network error'); }
    finally { setResetSending(false); }
  };

  const pwStrength = () => {
    if (!newPw) return null;
    const s = (newPw.length >= 12 ? 1 : 0) + (/[A-Z]/.test(newPw) ? 1 : 0) + (/[0-9]/.test(newPw) ? 1 : 0) + (/[^A-Za-z0-9]/.test(newPw) ? 1 : 0);
    if (s <= 1) return { label: t.pwStrengthWeak,   color: '#e74c3c', w: '25%' };
    if (s === 2) return { label: t.pwStrengthFair,   color: '#e67e22', w: '50%' };
    if (s === 3) return { label: t.pwStrengthGood,   color: '#f1c40f', w: '75%' };
    return             { label: t.pwStrengthStrong, color: '#2ecc71', w: '100%' };
  };
  const str = pwStrength();

  const formatDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      <button onClick={onBack} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.78rem', cursor: 'pointer', marginBottom: '1.5rem', minHeight: '36px' }}>
        ← Back
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.8rem,6vw,2.4rem)', color: 'var(--gold)', marginBottom: '1.5rem' }}>
        {t.profile}
      </h2>

      {/* Account details */}
      <div style={card}>
        <h3 style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem', fontWeight: 600 }}>
          {t.accountDetails}
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {[
            { label: t.username,     value: user?.username },
            { label: t.emailAddress, value: user?.email },
            { label: t.memberSince,  value: formatDate(user?.createdAt) },
            { label: 'Role',         value: user?.role === 'superuser' ? '👑 Superuser' : '👤 User' },
            { label: t.statusLabel,  value: user?.status?.replace('_', ' ') },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.4rem 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', flexShrink: 0 }}>{label}</span>
              <span style={{ fontSize: '0.88rem', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{value ?? '—'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Date format — controls how the unambiguous calendar date pickers (e.g. Add Fixture)
          display the currently-selected date; doesn't affect existing text like "19 August 2026"
          shown elsewhere in the app. */}
      <div style={card}>
        <h3 style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem', fontWeight: 600 }}>
          Date format
        </h3>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          {(['DMY', 'MDY', 'YMD'] as const).map(fmt => {
            const today = new Date();
            const dd = String(today.getDate()).padStart(2, '0');
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const yyyy = String(today.getFullYear());
            const label = fmt === 'DMY' ? 'DD/MM/YYYY' : fmt === 'MDY' ? 'MM/DD/YYYY' : 'YYYY-MM-DD';
            const example = fmt === 'DMY' ? `${dd}/${mm}/${yyyy}` : fmt === 'MDY' ? `${mm}/${dd}/${yyyy}` : `${yyyy}-${mm}-${dd}`;
            const active = (user?.dateFormat ?? 'DMY') === fmt;
            const busy = dateFmtSaving === fmt;
            return (
              <button
                key={fmt}
                onClick={() => handleSetDateFormat(fmt)}
                disabled={dateFmtSaving !== null}
                style={{
                  flex: '1 1 140px', textAlign: 'center', padding: '0.65rem 0.5rem', borderRadius: '8px', cursor: dateFmtSaving !== null ? 'default' : 'pointer',
                  background: active ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${active ? 'var(--gold)' : 'rgba(255,255,255,0.12)'}`,
                  opacity: dateFmtSaving !== null && !busy ? 0.5 : 1,
                }}
              >
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: active ? 'var(--gold)' : 'var(--chalk)' }}>{label}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>{busy ? '…' : example}</div>
              </button>
            );
          })}
        </div>
        {dateFmtErr && <p style={{ color: '#e74c3c', fontSize: '0.78rem', marginTop: '0.6rem' }}>{dateFmtErr}</p>}
      </div>

      {/* My stats — leagues/tournaments where this account has been linked to a player, with a
          played/won/lost/high-break breakdown per competition and a combined career summary. */}
      {leagueStats && (leagueStats.leagues.length > 0 || leagueStats.tournaments.length > 0) && (
        <div style={card}>
          <h3 style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem', fontWeight: 600 }}>
            {t.myStatsTitle}
          </h3>
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {([
              [t.statsPlayedLabel, leagueStats.summary.totalPlayed],
              [t.statsWonLabel, leagueStats.summary.totalWon],
              [t.statsLostLabel, leagueStats.summary.totalLost],
              [t.statsHighBreakLabel, leagueStats.summary.careerHighBreak],
            ] as const).map(([label, value]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', color: 'var(--gold)', lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.2rem' }}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {leagueStats.leagues.map(l => (
              <div
                key={`${l.leagueId}-${l.teamId}-${l.playerId}`}
                onClick={() => navigate(`/leagues/public/${l.leagueId}`)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', padding: '0.4rem 0', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
              >
                <span>🏆 <strong>{l.leagueName}</strong> — {l.divisionName} · {l.teamName}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: 'var(--chalk-dim)', whiteSpace: 'nowrap' }}>
                  {l.stats.won}W–{l.stats.lost}L{l.stats.highBreak > 0 ? ` · HB ${l.stats.highBreak}` : ''}
                  <span>›</span>
                </span>
              </div>
            ))}
            {leagueStats.tournaments.map(t2 => (
              <div key={`${t2.tournamentId}-${t2.playerId}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', padding: '0.4rem 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span>🔴 <strong>{t2.tournamentName}</strong></span>
                <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', whiteSpace: 'nowrap' }}>{t2.stats.won}W–{t2.stats.lost}L{t2.stats.highBreak > 0 ? ` · HB ${t2.stats.highBreak}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Change email */}
      <div style={card}>
        <h3 style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.35rem', fontWeight: 600 }}>
          {t.changeEmailTitle}
        </h3>
        <p style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', marginBottom: '1rem', lineHeight: 1.5 }}>
          Current: <strong style={{ color: 'var(--chalk)' }}>{user?.email}</strong>
        </p>
        <form onSubmit={handleChangeEmail} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <label style={labelSty}>{t.changeEmailNewLabel}</label>
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="new@example.com"
              required
              style={inputSty}
            />
          </div>
          {emailMsg && (
            <p style={{ fontSize: '0.8rem', color: emailMsg.type === 'ok' ? '#2ecc71' : '#e74c3c', margin: 0, lineHeight: 1.5 }}>
              {emailMsg.text}
            </p>
          )}
          <button
            type="submit"
            disabled={emailSending || !newEmail.trim()}
            style={goldBtn}
          >
            {emailSending ? t.saving : t.changeEmailSendBtn}
          </button>
        </form>
      </div>

      {/* Change password */}
      <div style={card}>
        <h3 style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem', fontWeight: 600 }}>
          {t.changePassword}
        </h3>
        <form onSubmit={handleChangePw} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <label style={labelSty}>{t.currentPassword}</label>
            <PasswordInput value={curPw} onChange={e => setCurPw(e.target.value)} autoComplete="current-password" required style={inputSty} />
          </div>
          <div>
            <label style={labelSty}>{t.newPassword}</label>
            <PasswordInput value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" required style={inputSty} />
            {str && (
              <>
                <div style={{ height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: str.w, background: str.color, borderRadius: '2px', transition: 'all 0.3s' }} />
                </div>
                <p style={{ fontSize: '0.68rem', color: str.color, marginTop: '3px' }}>{str.label}</p>
              </>
            )}
          </div>
          <div>
            <label style={labelSty}>{t.confirmNewPassword}</label>
            <PasswordInput value={confPw} onChange={e => setConfPw(e.target.value)} autoComplete="new-password" required
              style={{ ...inputSty, borderColor: confPw && confPw !== newPw ? '#e74c3c' : confPw && confPw === newPw ? '#2ecc71' : 'rgba(201,168,76,0.3)' }} />
          </div>

          {pwMsg && (
            <div style={{ background: pwMsg.type === 'ok' ? 'rgba(46,204,113,0.12)' : 'rgba(192,57,43,0.15)', border: `1px solid ${pwMsg.type === 'ok' ? 'rgba(46,204,113,0.35)' : 'rgba(192,57,43,0.4)'}`, borderRadius: '6px', padding: '0.55rem 0.85rem', color: pwMsg.type === 'ok' ? '#2ecc71' : '#e74c3c', fontSize: '0.82rem' }}>
              {pwMsg.type === 'ok' ? '✓ ' : '⚠ '}{pwMsg.text}
            </div>
          )}

          <button type="submit" disabled={pwSaving || !curPw || !newPw || !confPw} style={{ ...goldBtn, opacity: pwSaving || !curPw || !newPw || !confPw ? 0.5 : 1, alignSelf: 'flex-start' }}>
            {pwSaving ? 'Saving…' : t.changePassword}
          </button>
        </form>
      </div>

      {/* Reset password via email */}
      <div style={{ ...card, borderColor: 'rgba(52,152,219,0.2)', background: 'rgba(52,152,219,0.04)' }}>
        <h3 style={{ fontSize: '0.73rem', color: '#3498db', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem', fontWeight: 600 }}>
          📧 {t.forgotPassword}
        </h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', marginBottom: '0.85rem', lineHeight: 1.6 }}>
          {t.sendPasswordResetLink} <strong style={{ color: 'var(--chalk)' }}>{user?.email}</strong>.
          The link expires in 1 hour.
        </p>
        {resetSent ? (
          <div style={{ color: '#2ecc71', fontSize: '0.82rem', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '6px', padding: '0.55rem 0.85rem' }}>
            ✓ {t.resetLinkSent}
          </div>
        ) : (
          <>
            <button onClick={handleRequestReset} disabled={resetSending} style={{ background: 'rgba(52,152,219,0.15)', color: '#3498db', border: '1px solid rgba(52,152,219,0.35)', borderRadius: '8px', padding: '0.65rem 1.25rem', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', minHeight: '44px', opacity: resetSending ? 0.5 : 1 }}>
              {resetSending ? 'Sending…' : t.sendResetLink}
            </button>
            {resetErr && <p style={{ color: '#e74c3c', fontSize: '0.78rem', marginTop: '0.4rem' }}>{resetErr}</p>}
          </>
        )}
        {window.location.hostname === 'localhost' && (
          <p style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', marginTop: '0.6rem', opacity: 0.7 }}>
            🛠 Dev: Check API console for Ethereal preview URL.
          </p>
        )}
      </div>

      {/* Privacy policy link */}
      <div style={{ textAlign: 'center', paddingTop: '0.5rem' }}>
        <button onClick={() => onPrivacy?.()} style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline', padding: 0 }}>
          {t.privacyPolicy}
        </button>
      </div>
    </div>
  );
}
